<template>
  <div :class="`${classVariableName}-review-container`" ref="reviewCard" :style="{minHeight: isCarousel?'224px':''}">
    <div :class="`${classVariableName}-review-top`">
      <div :class="`${classVariableName}-review-top-left`">
        <ratingComponent :readOnly="true" :allowFraction="true" :initialRating="review.rating" size="s" />
      </div>

      <div v-if="
          !isEmpty(variantMap) &&
          !isEmpty(variantMap[`${review.alternateCode2}`])
        " :class="`${classVariableName}-review-top-right`">
        <img :class="`${classVariableName}-review-top-variant`"
          :src="variantMap[`${review.alternateCode2}`].medias[variantMap[`${review.alternateCode2}`].medias.length-1].url" />
        <div :class="`${classVariableName}-review-top-variant-name`"> {{
          variantMap[`${review.alternateCode2}`].color_name || variantMap[`${review.alternateCode2}`].name }} </div>
      </div>
    </div>

    <div v-if="!isEmpty(review.title)" :class="`${classVariableName}-review-heading-primary`">
      "{{ review.title }}"
    </div>

    <div v-if="!isEmpty(review.content)" :class="`${classVariableName}-review-heading-secondary`">
      {{ displayContent }}
      <a :class="`${classVariableName}-review-heading-primary`" href="#" @click.prevent="() => isExpanded=!isExpanded">
        {{ review.content.length > maxContentChar ? (!isExpanded ? 'Read more' : 'Read less') : ''}}
      </a>
    </div>

    <div v-if="!isEmpty(review.images)"
      :class="`${classVariableName}-img-grid`" :style="{marginTop: isCarousel?'auto':''}"
    >
      <div :class="`${classVariableName}-img-container ${classVariableName}-cursor-pointer`"
        v-for="(img, index) of review.images" :key="index">
        <img @click="() => openReviewDetails(review, index)" :style="{ width: '52px', height: '52px' }" :src="img.lowResUrl"
          alt="image preview" loading="lazy" :class="`${classVariableName}-image`" />
      </div>
    </div>
    <div :class="`${classVariableName}-review-bottom-container`" :style="{marginTop: isEmpty(review.images) && isCarousel?'auto':''}">
    <div :class="`${classVariableName}-review-bottom`">

      <div :class="`${classVariableName}-review-bottom-user`">
        <div v-if="!review.anonymous">{{ review.customerName || review.createdBy || "" }}</div>
        <CertifiedBuyer v-if="review.verified"/>
        <div v-if="review.verified" :class="`${classVariableName}-review-bottom-verified-tag`">|</div>
        <div v-if="review.verified" :class="`${classVariableName}-review-bottom-verified-tag`">Verified buyer</div>
      </div>

      <fdk-accounts v-slot="accountsData" :class="`${classVariableName}-review-bottom-actions`">
        <div v-if="
            !isEmpty(validationConfig) &&
            !isEmpty(validationConfig.upvote) &&
            !validationConfig.upvote.hidden
          " :class="`${classVariableName}-vote ${vote ? '' : `${classVariableName}-vote-disabled`}`">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
            :class="`${classVariableName}-cursor-pointer`" @click="() => sendVote('UPVOTE', accountsData)">
            <rect x="0.5" y="0.5" width="23" height="23" rx="3.5"
              :fill="selectedAction === 'UPVOTE' ? '#211A1E' : '#F9F9F9'"
              :stroke="selectedAction === 'UPVOTE' ? '' : '#EEEEEE'" />
            <g clip-path="url(#clip0_590_34903)">
              <path
                d="M10.125 17.625H15.75C16.2687 17.625 16.7125 17.3125 16.9 16.8625L18.7875 12.4562C18.8437 12.3125 18.875 12.1625 18.875 12V10.75C18.875 10.0625 18.6855 10.1252 17.998 10.1252H13.123L14.275 6.64375L14.2937 6.44375C14.2937 6.1875 14.1875 5.95 14.0187 5.78125L13.3562 5.125L9.2375 9.24375C9.0125 9.46875 8.875 9.78125 8.875 10.125V16.375C8.875 17.0625 9.4375 17.625 10.125 17.625ZM9.74805 9.75024L13.3562 6.37524L12 10.75H17.998V12L16.123 16.8752H9.74805V9.75024ZM5.125 10.125H7.625V17.625H5.125V10.125Z"
                :fill="selectedAction === 'UPVOTE' ? 'white' : 'black'" />
            </g>
            <defs>
              <clipPath id="clip0_590_34903">
                <rect x="4.5" y="4.5" width="15" height="15" rx="2" fill="white" />
              </clipPath>
            </defs>
          </svg>
          <span :class="`${classVariableName}-vote-text`">
            {{ review.upvoteCount }}
          </span>
        </div>
      </fdk-accounts>
    </div>
    <div :class="`${classVariableName}-review-bottom-details`">
      <div v-if="!isEmpty(review.origin)" :class="`${classVariableName}-review-bottom-origin`">
        From {{ review.origin }}
      </div>
      <div id="createdAt" :class="`${classVariableName}-review-by`">
        {{ formatDate(review.createdAt) }}{{ " " }}
      </div>
    </div>
    </div>
    <component :is="imageReviewModalComponent" v-if="detailedReview" :product="product" :hasNext="false" :hasPrev="false"
      :currentIndex="imageCurrentIndex" :detailedGalleryFlow="detailedGallery" :openDetailedGallery="() => {
        detailedReview = false;
        detailedGallery = true;
      }" :onClose="() => closePreviews()" :review="currentReviewDetails" :validationConfig="validationConfig" :vote="vote"
      :setAlert="(message, type) => setAlert(message, type)" />
  </div>
</template>

<script>
import { defineComponent } from "@vue/composition-api";
import customerServices from "../services/customer-review";
import { isEmpty, classVariableName, formatDate, getEventData, emitEvent } from "../utils";
import CUSTOM_EVENTS from "../constants";
import ratingComponent from "./common/StarRating.vue";
import CertifiedBuyer from "./common/CertifiedBuyer.vue";
import Tooltip from "./common/Tooltip.vue";
import Modal from "./common/Modal.vue";

export default defineComponent({
  name: "reviewComponent",
  mounted() {
    window.FPI.state.product.variants.subscribe(this.getVariants.bind(this));
    this.getDivWidth();
  },
  destroy() {
    window.FPI.state.product.variants.unsubscribe(this.getVariants.bind(this));
  },
  components: {
    ratingComponent,
    CertifiedBuyer,
    Tooltip,
    Modal,
  },
  data() {
    return {
      variantMap: null,
      selectedAction: undefined,
      abuseReported: undefined,
      showAbuseModal: false,
      abuseContent: "",
      abuseContentError: null,
      classVariableName: classVariableName,
      abuseReportValidations:
        this.validationConfig && this.validationConfig.abuseReport,

      //detailed image modal
      detailedGallery: false,
      detailedReview: false,
      imageCurrentIndex: 0,
      currentReviewDetails: null,
      imageReviewModalComponent: null,
      maxContentChar: 55
    };
  },
  props: {
    product: Object,
    vote: Boolean,
    review: {
      type: Object,
      default: {},
    },
    validationConfig: {
      type: Object,
      default: {},
    },
    mobView: {
      type: Boolean,
      default: false
    },
    isCarousel: {
      type: Boolean,
      default: false
    },
    isExpanded: {
      type: Boolean,
      default: false
    },
    setAlert: Function,
  },
  watch: {
    review(newValue, oldValue) {
      if (newValue.id != oldValue.id) {
        this.selectedAction = null;
      }
    },
  },
  created() {
    this.loadImageReviewModal();
  },
  computed: {
    displayContent() {
      if (this.isExpanded) {
        return this.review.content;
      }
      if (this.review.content.length <= this.maxContentChar) {
        return this.review.content;
      }
      return this.review.content.slice(0, this.maxContentChar) + '...';
    },
  },
  methods: {
    async closePreviews() {
      emitEvent(CUSTOM_EVENTS.REVIEWS_WITH_IMAGES_CLOSE_PREVIEW, getEventData(!isEmpty(Object.keys(window.FPI.state.user._data)), 'image_gallery', this.product, this.review));
      this.detailedGallery = false;
      this.detailedReview = false;
    },
    async loadImageReviewModal() {
      if (this.mobView) {
        this.imageReviewModalComponent = (await import('./mob/ImageReviewModal.vue')).default;
      } else {
        this.imageReviewModalComponent = (await import('./gallery/ImageReviewModal.vue')).default;
      }
    },

    getDivWidth() {
      const reviewCard = this.$refs.reviewCard;
      if (reviewCard) {
        this.maxContentChar = Math.floor((reviewCard.clientWidth - 38) / 8.4) * 2;
      }
    },

    formatDate(val) {
      var date = new Date(val);
      return formatDate(date);
    },

    isEmpty(val) {
      return isEmpty(val);
    },
    
    async getVariants(data) {
      let variants = data.variants;
      if (!isEmpty(variants)) {
        let colorVariants = variants.filter((types) => types.key === "color");
        if (!isEmpty(colorVariants)) {
          this.variantMap = colorVariants[0].items.reduce((vmap, obj) => {
              vmap[obj.uid] = obj;
              return vmap;
            }, {});
        }
      }
    },

    onCloseModal() {
      this.showAbuseModal = false;
      this.abuseContent = "";
      this.abuseContentError = null;
    },

    async openReviewDetails(imageReview, imageIndex) {
      emitEvent(CUSTOM_EVENTS.REVIEWS_WITH_IMAGES_CLICK_ON_PAGE, getEventData(!isEmpty(Object.keys(window.FPI.state.user._data)), 'PDP', this.product, imageReview));
      this.detailedReview = true;
      this.currentReviewDetails = imageReview;
      this.currentReviewDetails.imageList = imageReview.images;
      this.imageCurrentIndex = imageIndex;
    },

    async sendVote(action, accountsData) {
      if (!this.vote || action === this.selectedAction) return;
      emitEvent(CUSTOM_EVENTS.REVIEW_LIKE, getEventData(accountsData.is_logged_in, 'PDP', this.product, this.review));
      if (!accountsData.is_logged_in) {
        accountsData.openLogin();
        return;
      }

      let data = {
        contentId: this.review.id,
        vote: action,
      };

      await customerServices
        .voteReview(data)
        .then((data) => {
          let res = data.data;
          if (res.status == 401) {
            accountsData.openLogin();
            return;
          }
          if (action === "DOWNVOTE") {
            this.review.downvoteCount = parseInt(this.review.downvoteCount) + 1;
            if (!isEmpty(this.selectedAction)) {
              this.review.upvoteCount = parseInt(this.review.upvoteCount) - 1;
            }
            this.selectedAction = action;
          } else if (action === "UPVOTE") {
            this.review.upvoteCount = parseInt(this.review.upvoteCount) + 1;
            if (!isEmpty(this.selectedAction)) {
              this.review.downvoteCount =
                parseInt(this.review.downvoteCount) - 1;
            }
            this.selectedAction = action;
          }
        })
        .catch((error) => {
          this.setAlert(error.response.data.info.resultInfo.message, "error");
        });
    },
  },
});
</script>

<style lang="scss" scoped>
@import "../assets/Review.scss";
</style>
